import { url } from '@api-config';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Api from 'Api/Api';
import { initIngestionSchedule } from 'Modules/Studio/DataSources/components/DataSourceForms/helpers/getDataSourceIngestionSchedule';
import { useSolutionContext } from '../../context/Solution/SolutionContext';
import { IngestionScheduleFormData } from 'Modules/Studio/components/IngestionOverrideForm/IngestionOverrideForm';

export const globalIngestionScheduleKeys = {
  all: ['globalIngestionSchedule'] as const,
  detail: (solutionId: number) => [...globalIngestionScheduleKeys.all, solutionId] as const,
};

export function useGlobalIngestionSchedule() {
  const { solution } = useSolutionContext();
  const solutionId = solution?.id;
  const queryClient = useQueryClient();

  const {
    data: scheduleInfo,
    isLoading,
    isSuccess,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: globalIngestionScheduleKeys.detail(solutionId!),
    queryFn: () => Api.get(`${url.solution}/${solutionId}/ingestion/schedule`),
    enabled: !!solutionId,
    select: (response) => {
      const scheduleExists = response.length > 0;
      const ingestionSchedule = scheduleExists
        ? [{ ...response[0], override: true }]
        : [...initIngestionSchedule()];
      return { ingestionSchedule, scheduleExists };
    },
    staleTime: Infinity,
  });

  const updateScheduleMutation = useMutation({
    mutationFn: (data: IngestionScheduleFormData) => {
      if (!solutionId) {
        throw new Error('Solution ID is required to update the schedule.');
      }
      const scheduleExist = data.ingestionSchedule[0].id;
      const method = scheduleExist ? 'put' : 'post';
      const baseURL = `${url.solution}/${solutionId}/ingestion/schedule`;
      const schedulePayload = data.ingestionSchedule[0];
      const requestUrl = scheduleExist ? `${baseURL}/${schedulePayload.id}` : baseURL;
      return Api[method](requestUrl, schedulePayload);
    },
    onSuccess: (newSchedule) => {
      if (!solutionId) return;
      const queryKey = globalIngestionScheduleKeys.detail(solutionId);
      queryClient.setQueryData(queryKey, [newSchedule]);
      queryClient.invalidateQueries({ queryKey: globalIngestionScheduleKeys.all });
    },
  });

  const deleteScheduleMutation = useMutation({
    mutationFn: (scheduleId: number | string | undefined) => {
      if (!solutionId) {
        throw new Error('Solution ID is required to delete the schedule.');
      }
      if (!scheduleId) {
        throw new Error('Schedule ID is required to delete the schedule.');
      }
      return Api.delete(`${url.solution}/${solutionId}/ingestion/schedule/${scheduleId}`);
    },
    onSuccess: () => {
      if (!solutionId) return;
      const queryKey = globalIngestionScheduleKeys.detail(solutionId);
      queryClient.setQueryData(queryKey, []);
      queryClient.invalidateQueries({ queryKey: globalIngestionScheduleKeys.all });
    },
  });

  return {
    ingestionSchedule: scheduleInfo?.ingestionSchedule,
    scheduleExists: scheduleInfo?.scheduleExists,
    isLoading,
    hasError: isError,
    error,
    isSuccess,
    fetchSchedule: refetch,

    updateSchedule: updateScheduleMutation.mutate,
    isUpdating: updateScheduleMutation.isPending,
    hasErrorUpdating: updateScheduleMutation.isError,
    isSuccessfulUpdating: updateScheduleMutation.isSuccess,

    deleteSchedule: deleteScheduleMutation.mutate,
    isDeleting: deleteScheduleMutation.isPending,
    hasErrorDeleting: deleteScheduleMutation.isError,
    isSuccessfulDeleting: deleteScheduleMutation.isSuccess,
  };
}
